import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

const LOOKUP_LIMIT = 50;
const LIST_MIN_WIDTH = 224;
const LIST_MAX_HEIGHT = 256;

// Users lookup: type any part of a name, pick the person. Only names are
// shown; the email is the value underneath. `options` is
// [{ email, label, jobTitle }] — see
// employeeOptionsFromDirectory. The × clears back to `placeholder`
// (e.g. "All employees" on the Absence dashboard's filters) unless
// `clearable` is false (a required answer, e.g. the edit form's Employee).
export function employeeOptionsFromDirectory(directory, extraEmails = []) {
  const byEmail = new Map();
  for (const p of directory?.people || []) {
    const email = (p.email || '').toLowerCase();
    if (email) byEmail.set(email, { email, label: p.display_name || email, jobTitle: p.job_title || null });
  }
  // Anyone on a record who isn't in the directory any more (a leaver) —
  // still selectable, so no record becomes unfilterable or uneditable.
  for (const raw of extraEmails) {
    const email = (raw || '').toLowerCase();
    if (email && !byEmail.has(email)) byEmail.set(email, { email, label: email, jobTitle: null });
  }
  return [...byEmail.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function EmployeeLookup({ value, onChange, options, placeholder = 'Search employees...', clearable = true, className = 'w-48' }) {
  const current = (value || '').toLowerCase();
  const selected = options.find((o) => o.email === current);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const found = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return found.slice(0, LOOKUP_LIMIT);
  }, [options, query]);

  function pick(email) {
    onChange(email);
    setQuery('');
    setOpen(false);
  }

  // Where the (portalled) list goes: under the input, at least as wide as
  // it, flipped above when there isn't room below. Kept in step while the
  // page or any section scrolls, or the window resizes.
  const inputRef = useRef(null);
  const [listStyle, setListStyle] = useState(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    function place() {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.max(r.width, LIST_MIN_WIDTH);
      const left = Math.min(r.left, window.innerWidth - width - 8);
      const below = window.innerHeight - r.bottom - 8;
      const above = r.top - 8;
      const flip = below < LIST_MAX_HEIGHT && above > below;
      setListStyle({
        left: Math.max(8, left),
        width,
        maxHeight: Math.min(LIST_MAX_HEIGHT, flip ? above : below),
        ...(flip ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <div className={`relative shrink-0 ${className}`}>
      <input
        ref={inputRef}
        value={open ? query : selected?.label || value || ''}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter') {
            e.preventDefault();
            if (matches[0]) pick(matches[0].email);
          }
        }}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 py-1.5 pl-2 pr-6 text-sm text-gray-700 focus:border-brand focus:ring-brand"
      />
      {clearable && selected && !open && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear"
          className="absolute inset-y-0 right-1 flex items-center text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
      {open && listStyle && createPortal(
        // Portalled to <body> with fixed positioning: the sections this sits
        // in clip their overflow (for the rounded corners), which cut the
        // list off. onMouseDown, not onClick: picking must land before the
        // input's onBlur closes the list.
        <ul style={listStyle} className="fixed z-50 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 text-xs shadow-lg">
          {matches.map((o) => (
            <li key={o.email}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(o.email); }}
                className={`block w-full px-3 py-1.5 text-left hover:bg-brand-light ${o.email === current ? 'bg-brand-light' : ''}`}
              >
                <span className="block text-gray-900">{o.label}</span>
              </button>
            </li>
          ))}
          {matches.length === 0 && <li className="px-3 py-1.5 text-gray-400">No one matches.</li>}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default EmployeeLookup;
